/**
 * The Iguana status panel, in our shell.
 *
 * "Received" and "In log" are deliberately named differently. Received is a
 * since-boot channel counter; In log is the ring buffer. Clear empties the
 * buffer and leaves the counters alone by design, so after a Clear the two stop
 * agreeing permanently. Two similar numbers that disagree read as a bug unless
 * the labels say why.
 */
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { Hl7Status } from '@/lib/dataLayer/types'

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  return h < 24 ? `${h}h ${m % 60}m` : `${Math.floor(h / 24)}d ${h % 24}h`
}

function Stat({
  label,
  value,
  hint,
  muted,
}: {
  label: string
  value: string
  hint?: string
  muted?: boolean
}) {
  const body = (
    <div className="min-w-[5.5rem] px-4 py-2.5">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn('mt-0.5 font-mono text-lg leading-none', muted && 'text-muted-foreground')}>
        {value}
      </p>
    </div>
  )

  if (!hint) return body

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-default">{body}</div>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  )
}

export function DataLayerStatusStrip({ status }: { status: Hl7Status }) {
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap items-stretch divide-x divide-border/60 rounded-lg border border-border/60 bg-card">
        <Stat
          label="Connections"
          value={String(status.openConnections)}
          hint={`Open peer connections. ${status.healthChecks} load-balancer probe${status.healthChecks === 1 ? '' : 's'} excluded.`}
        />
        <Stat
          label="Received"
          value={String(status.messages.in)}
          hint="Inbound messages since the service started. Clear does not reset this."
        />
        <Stat
          label="Sent"
          value={String(status.messages.out)}
          hint="Outbound messages since the service started."
        />
        <Stat
          label="Rate"
          value={status.messagesPerSec.toFixed(2)}
          hint="Messages per second over a trailing 60s window, both directions."
        />
        <Stat
          label="In log"
          value={`${status.stored} / ${status.storeCapacity}`}
          hint="Packets currently retained in memory. The buffer is bounded and never written to disk."
          muted
        />
        <Stat label="Uptime" value={formatUptime(status.uptimeSec)} muted />
      </div>
    </TooltipProvider>
  )
}
